// =============================================================================
// app/src/lib/enderecos.ts — o ENDEREÇO de cada coisa na loja (regra que o dono
// aprovou em 25/09/2026 para os ganchos e estendeu à loja inteira).
//
//   TIPO-ÁREA-LUGAR-…   do geral para o particular, como rua, número e apto.
//
//   GC-FR-D-01-05    gancho de calota · frente · parede da direita · fileira 1 · 5º gancho
//   ES-CO-1D-01-04   estante · corredor 1, lado direito · bloco 1 · 4ª prateleira
//   PR-FR-D2-03      prateleira de parede · frente · 2ª da parede da direita · 3º nível
//
// Conta-se de CIMA para BAIXO e a partir de ONDE SE CHEGA: de frente para uma
// parede, da esquerda para a direita; num corredor, da entrada para o fundo.
// (A loja já contava assim: o 10W40 do PR-FR-D-01 está na prateleira de cima.)
//
// O começo do código é sempre um LUGAR: "ES-CO-1D" é o lado direito do
// corredor 1 inteiro, e é por isso que o mapa acha a peça pelo prefixo.
// =============================================================================
import type { CodigoZona } from './loja3d';

export const TIPOS_DE_LUGAR = {
  GC: 'Gancho de calota',
  GA: 'Gancho',
  PR: 'Prateleira de parede',
  ES: 'Estante',
  BC: 'Balcão',
  CH: 'No chão',
  PN: 'Painel',
  MS: 'Mesa',
  CX: 'Caixas',
} as const;
export type TipoDeLugar = keyof typeof TIPOS_DE_LUGAR;

export const AREAS = {
  FR: { nome: 'frente', zona: 'Z1' },
  CO: { nome: 'corredores', zona: 'Z2' },
  FU: { nome: 'fundos', zona: 'Z3' },
  AN: { nome: '2º andar', zona: 'Z4' },
  SO: { nome: 'sótão', zona: 'Z5' },
} as const satisfies Record<string, { nome: string; zona: CodigoZona }>;
export type Area = keyof typeof AREAS;

/** As paredes da frente (Z1), pela letra. */
export const PAREDES_DA_FRENTE: Record<string, string> = {
  E: 'parede da entrada (esquerda)',
  C: 'coluna da porta da escada',
  P: 'em cima da porta',
  D: 'parede da direita',
  B: 'painel das bicicletas',
  V: 'parede vermelha',
  // 01/10/2026: a arara de tapetes, junto à vitrine (ES-FR-T-01, ES-FR-T-02).
  T: 'arara de tapetes',
};

/** Os balcões da frente, pela letra. */
export const BALCOES: Record<string, string> = {
  E: 'balcão da esquerda',
  C: 'balcão do caixa',
};

/** A marca da placa no teto de cada corredor da Z2. */
export const CORREDORES: Record<string, string> = { 1: 'Fiat', 2: 'Ford', 3: 'Chevrolet', 4: 'VW' };

const ordinal = (n: number, genero: 'o' | 'a') => `${n}${genero === 'a' ? 'ª' : 'º'}`;
const numero = (s: string | undefined): number | null => (s && /^\d+$/.test(s) ? Number(s) : null);

/** As partes do código, se ele segue a regra (TIPO e ÁREA conhecidos). */
function partes(codigo: string | null | undefined): { tipo: TipoDeLugar; area: Area; resto: string[] } | null {
  const p = (codigo ?? '').trim().toUpperCase().split(/\s*-\s*/).filter(Boolean);
  const [tipo, area, ...resto] = p;
  if (!tipo || !area || !(tipo in TIPOS_DE_LUGAR) || !(area in AREAS)) return null;
  return { tipo: tipo as TipoDeLugar, area: area as Area, resto };
}

/** A zona do mapa que a ÁREA do endereço aponta (ES-CO-… está na Z2), ou null. */
export function zonaDoEndereco(codigo: string | null | undefined): CodigoZona | null {
  const p = partes(codigo);
  return p ? AREAS[p.area].zona : null;
}

/** O lugar (3ª parte) dito por extenso. */
function lugarPorExtenso(tipo: TipoDeLugar, area: Area, lugar: string): string {
  if (area === 'CO') {
    const m = /^(\d+)([ED])$/.exec(lugar);
    if (m) {
      const marca = CORREDORES[m[1]!];
      return `corredor ${m[1]}${marca ? ` (${marca})` : ''}, lado ${m[2] === 'E' ? 'esquerdo' : 'direito'}`;
    }
    if (lugar === 'CZ') return 'junto à cozinha';
  }
  if (area === 'FR') {
    // No chão, na frente de um balcão: CH-FR-BE = junto ao balcão da esquerda.
    const balcao = tipo === 'CH' ? /^B([A-Z])$/.exec(lugar) : null;
    if (balcao && BALCOES[balcao[1]!]) return `junto ao ${BALCOES[balcao[1]!]}`;
    const m = /^([A-Z])(\d*)$/.exec(lugar);
    if (m) {
      const nome = (tipo === 'BC' ? BALCOES : PAREDES_DA_FRENTE)[m[1]!];
      if (nome) {
        const n = numero(m[2]);
        const onde = tipo === 'CH' ? `junto à ${nome}` : nome;
        return n ? `${onde}, a ${ordinal(n, 'a')} da esquerda` : onde;
      }
    }
  }
  const n = numero(lugar);
  if (n) {
    const coisa = tipo === 'MS' ? 'mesa' : tipo === 'CX' ? 'pilha de caixas' : 'estante';
    return `${coisa} ${n}`;
  }
  return lugar;
}

/**
 * O endereço por extenso, para quem não decorou a regra:
 * "ES-CO-1D-01-04" → "Estante · corredor 1 (Fiat), lado direito · bloco 1 (o da entrada) · 4ª prateleira de cima".
 * Código fora da regra ("loja", "G-1-4") devolve null.
 */
export function descreverEndereco(codigo: string | null | undefined): string | null {
  const p = partes(codigo);
  if (!p) return null;
  const { tipo, area, resto } = p;
  const trechos: string[] = [TIPOS_DE_LUGAR[tipo]];
  const [lugar, ...numeros] = resto;
  if (!lugar) return [...trechos, AREAS[area].nome].join(' · ');

  if (area !== 'CO') trechos.push(AREAS[area].nome);
  trechos.push(lugarPorExtenso(tipo, area, lugar));

  const [a, b] = numeros.map(numero);
  if (area === 'FR' && lugar === 'T') {
    // A arara não tem prateleira: cada número é um cabide.
    if (a) trechos.push(`posição ${a}`);
  } else if (tipo === 'PN') {
    // O painel vermelho dos emblemas (01/10/2026): fileira de cima para baixo, posição da esquerda.
    if (a) trechos.push(`fileira ${a}${a === 1 ? ' (a de cima)' : ''}`);
    if (b) trechos.push(`${ordinal(b, 'a')} posição da esquerda`);
  } else if (tipo === 'GC' || tipo === 'GA') {
    if (a) trechos.push(`fileira ${a}${a === 1 ? ' (a de cima)' : ''}`);
    if (b) trechos.push(`${ordinal(b, 'o')} gancho da esquerda`);
  } else if (tipo === 'ES' && area === 'CO') {
    if (a) trechos.push(`bloco ${a}${a === 1 ? ' (o da entrada)' : ''}`);
    if (b) trechos.push(`${ordinal(b, 'a')} prateleira de cima`);
  } else if (tipo === 'CH') {
    if (a) trechos.push(`${ordinal(a, 'a')} pilha, contando da frente`);
  } else if (tipo === 'PR' || tipo === 'ES' || tipo === 'BC') {
    if (a) trechos.push(`${ordinal(a, 'a')} prateleira de cima`);
    if (b) trechos.push(`posição ${b}`);
  } else {
    numeros.forEach((n) => trechos.push(n));
  }
  return trechos.join(' · ');
}

// --- A lista dos endereços e as placas -----------------------------------------

/** Um lugar da loja com endereço, como a lista "Endereços da loja" mostra. */
export interface LugarComEndereco {
  codigo: string;
  descricao: string;
  nome: string;
  /** O objeto do mapa que se acende ao tocar (numa parede de ganchos, o 1º). */
  origem: string;
  area: Area;
  pecas: number;
  /** Numa parede de ganchos, quantos ganchos ela tem; nos outros lugares, 0. */
  ganchos: number;
}

const ORDEM_DAS_AREAS = Object.keys(AREAS) as Area[];
const ehDeGancho = (tipo: TipoDeLugar) => tipo === 'GC' || tipo === 'GA';

/**
 * Todos os lugares com endereço na regra, por área e por código. Os ganchos
 * de uma parede viram UMA linha (GC-FR-D, 16 ganchos) — são dezenas, e quem
 * quer o gancho toca na parede e vê a vista de frente.
 */
export function lugaresComEndereco(
  objetos: ReadonlyArray<{ origem: string; codigo?: string; nome?: string }>,
  pecasPorObjeto: ReadonlyMap<string, number>,
): LugarComEndereco[] {
  const porCodigo = new Map<string, LugarComEndereco>();
  const ordenados = [...objetos].sort((a, b) => (a.codigo ?? '').localeCompare(b.codigo ?? ''));
  for (const o of ordenados) {
    const codigo = o.codigo?.trim().toUpperCase();
    const p = partes(codigo);
    if (!codigo || !p) continue;
    const pecas = pecasPorObjeto.get(o.origem) ?? 0;
    if (ehDeGancho(p.tipo) && p.resto.length >= 3) {
      const parede = [p.tipo, p.area, p.resto[0]].join('-');
      const ja = porCodigo.get(parede);
      if (ja) { ja.ganchos += 1; ja.pecas += pecas; continue; }
      porCodigo.set(parede, {
        codigo: parede, descricao: descreverEndereco(parede) ?? '', nome: o.nome ?? '', origem: o.origem,
        area: p.area, pecas, ganchos: 1,
      });
      continue;
    }
    if (porCodigo.has(codigo)) continue;
    porCodigo.set(codigo, {
      codigo, descricao: descreverEndereco(codigo) ?? '', nome: o.nome ?? '', origem: o.origem, area: p.area, pecas, ganchos: 0,
    });
  }
  return [...porCodigo.values()].sort((a, b) =>
    ORDEM_DAS_AREAS.indexOf(a.area) - ORDEM_DAS_AREAS.indexOf(b.area) || a.codigo.localeCompare(b.codigo));
}

/** Uma placa para imprimir e colar no lugar. */
export interface Placa {
  codigo: string;
  descricao: string;
  nota: string;
  area: Area;
}

/**
 * As placas da loja: uma por FILEIRA de ganchos (GC-FR-D-01) e uma por lugar
 * com código na regra. Estante que tinha letra antiga ("era G", no nome) leva
 * a letra na placa, para a equipe achar o caminho enquanto se acostuma.
 */
export function placasDaPlanta(objetos: ReadonlyArray<{ codigo?: string; nome?: string }>): Placa[] {
  const fileiras = new Map<string, number>();
  const placas: Placa[] = [];
  for (const o of objetos) {
    const codigo = o.codigo?.trim().toUpperCase();
    const p = partes(codigo);
    if (!codigo || !p) continue;
    if (ehDeGancho(p.tipo) && p.resto.length >= 3) {
      const fileira = [p.tipo, p.area, ...p.resto.slice(0, 2)].join('-');
      fileiras.set(fileira, (fileiras.get(fileira) ?? 0) + 1);
      continue;
    }
    const era = /\(era ([A-Z])\)/.exec(o.nome ?? '');
    placas.push({ codigo, descricao: descreverEndereco(codigo) ?? '', nota: era ? `era a estante ${era[1]}` : (o.nome ?? ''), area: p.area });
  }
  for (const [codigo, n] of fileiras) {
    placas.push({
      codigo, descricao: descreverEndereco(codigo) ?? '', area: partes(codigo)!.area,
      nota: n === 1 ? '1 gancho' : `${n} ganchos: 1 a ${n}, da esquerda para a direita`,
    });
  }
  return placas.sort((a, b) => ORDEM_DAS_AREAS.indexOf(a.area) - ORDEM_DAS_AREAS.indexOf(b.area) || a.codigo.localeCompare(b.codigo));
}
